import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { ApiCardType } from "@/store/slices/dashboardSlice";
import { updateCardFavoriteStatus, toggleCardFavorite, fetchUserDashboardCards } from "@/store/slices/dashboardSlice";
import type { AppDispatch, RootState } from "@/store/store";

type CardVariant = "default" | "highlight" | "warning" | "favourite";

export const useDashboardCardFavorite = (cardType: ApiCardType) => { // NOSONAR
  const dispatch = useDispatch<AppDispatch>();
  const cardMeta = useSelector((state: RootState) =>
    state.dashboard.cards.find((card) => card.type === cardType)
  );
  const isLoading = useSelector((state: RootState) => state.dashboard.loading);
  const [isUpdating, setIsUpdating] = useState(false);

  const toggleFavourite = useCallback(async () => {
    if (isLoading) {
      console.warn(`Cards are still loading for card type: ${cardType}`);
      return;
    }
    
    // If cardMeta is missing, try to refetch cards first
    if (!cardMeta) {
      console.warn(`Card metadata not found for card type: ${cardType}. Attempting to refetch...`);
      try {
        const result = await dispatch(fetchUserDashboardCards()).unwrap();
        const refreshedCardMeta = result.cards.find((card) => card.type === cardType);
        if (!refreshedCardMeta) {
          console.error(`Card ${cardType} still not found after refetch. Available card types:`, result.cards.map(c => c.type));
          return;
        }
        // Retry with the refreshed cardMeta
        const previousFavoriteState = refreshedCardMeta.isFavorite;
        dispatch(toggleCardFavorite({ cardId: refreshedCardMeta.id }));
        setIsUpdating(true);
        try {
          await dispatch(
            updateCardFavoriteStatus({
              cardInstanceId: refreshedCardMeta.id,
              cardId: refreshedCardMeta.cardId,
              nextValue: !previousFavoriteState,
            })
          ).unwrap();
        } catch (error) {
          console.error(`Failed to toggle favourite state for card ${cardType}`, error);
          dispatch(toggleCardFavorite({ cardId: refreshedCardMeta.id }));
        } finally {
          setIsUpdating(false);
        }
        return;
      } catch (error) {
        console.error(`Failed to refetch cards for ${cardType}`, error);
        return;
      }
    }
    
    if (isUpdating) {
      console.warn(`Already updating favourite for card type: ${cardType}`);
      return;
    }

    // Optimistic update: immediately update UI
    const previousFavoriteState = cardMeta.isFavorite;
    dispatch(toggleCardFavorite({ cardId: cardMeta.id }));

    setIsUpdating(true);
    try {
      // Make API call in the background
      await dispatch(
        updateCardFavoriteStatus({
          cardInstanceId: cardMeta.id,
          cardId: cardMeta.cardId,
          nextValue: !previousFavoriteState,
        })
      ).unwrap();
    } catch (error) {
      // Revert optimistic update on error
      console.error(
        `Failed to toggle favourite state for card ${cardType}`,
        error
      );
      // Revert by toggling back
      dispatch(toggleCardFavorite({ cardId: cardMeta.id }));
    } finally {
      setIsUpdating(false);
    }
  }, [cardMeta, cardType, dispatch, isUpdating, isLoading]);

  const cardVariant: CardVariant = cardMeta?.isFavorite ? "favourite" : "default";

  return {
    isFavourite: cardMeta?.isFavorite ?? false,
    cardVariant,
    toggleFavourite,
    favouriteDisabled: isUpdating || isLoading,
  };
};

